import { gql } from '@apollo/client';
import { client, getGraphQLEndpoint } from './apolloClient';
import { UserRole } from './context/AuthContext';
import { isValidRole } from './context/AuthContext';
import { describeDirectUploadFailure } from './services/r2AssetUpload';
import { currentIdToken } from './services/freshIdToken';
import { MULTIPART_THRESHOLD_BYTES } from './services/multipartUpload';
import { graphqlTransport, uploadRevisionInParts } from './services/multipartUploadApi';
import { debugLog } from './utils/debugLog';
import { MULTICAM_CONFLICT_CODE, MULTICAM_CONFLICT_MESSAGE, MulticamConflictError } from './utils/multicamConflict';
import type { SpeakingActivity } from './utils/speakerActivity';

const wait = (milliseconds: number) => new Promise(resolve => setTimeout(resolve, milliseconds));

async function withTransientRetry<T>(label: string, operation: () => Promise<T>, attempts = 3): Promise<T> {
  let lastError: unknown;
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      return await operation();
    } catch (error: any) {
      lastError = error;
      const message = String(error?.message || error || '').toLowerCase();
      const nonRetryable = message.includes('auth') || message.includes('forbidden') || message.includes('permission');
      if (attempt === attempts || nonRetryable) break;
      console.warn(`${label} attempt ${attempt} failed; retrying`, error);
      await wait(attempt * 750);
    }
  }
  throw lastError;
}

export const getUserRole = async (userId: string): Promise<UserRole> => {
  try {
    debugLog('Fetching user role for userId:', userId);
    const result = await client.query({
      query: gql`
        query GetUserRole($userId: String!) {
          getUserRole(userId: $userId)
        }
      `,
      variables: { userId }
    });
    debugLog('Query result:', result);

    if (!result.data || !result.data.getUserRole) {
      throw new Error('No data returned from getUserRole query');
    }

    const role = result.data.getUserRole;
    if (isValidRole(role)) {
      return role;
    } else {
      throw new Error(`Invalid role returned: ${role}`);
    }
  } catch (error) {
    console.error('Error in getUserRole:', error);
    throw error;
  }
};

export const setUserRole = async (userId: string, email: string, role: UserRole, token?: string) => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation SetUserRole($userId: String!, $email: String!, $role: String!) {
          setUserRole(userId: $userId, email: $email, role: $role) {
            uid
            email
            role
          }
        }
      `,
      variables: { userId, email, role },
      // Remove manual token - let authLink handle it
    });
    return data.setUserRole;
  } catch (error: any) {
    console.error('Error setting user role:', error);
    throw new Error(error.message);
  }
};

export const updateUserRole = async (userId: string, email: string, role: UserRole, token: string) => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation UpdateUserRole($userId: String!, $email: String!, $role: String!) {
          updateUserRole(userId: $userId, email: $email, role: $role) {
            uid
            email
            role
          }
        }
      `,
      variables: { userId, email, role },
      context: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    });
    return data.updateUserRole;
  } catch (error: any) {
    console.error('Error updating user role:', error);
    throw new Error(error.message);
  }
};

const UPLOAD_VIDEO_MUTATION = gql`
  mutation UploadVideo($url: String!, $name: String!, $size: Float!, $duration: String!, $storageProvider: String, $storageKey: String, $frameRate: Float) {
    uploadVideo(url: $url, name: $name, size: $size, duration: $duration, storageProvider: $storageProvider, storageKey: $storageKey, frameRate: $frameRate) {
      id
      url
      userId
      name
      size
      duration
    }
  }
`;

const ADD_ANNOTATION_MUTATION = gql`
  mutation AddAnnotation($videoId: String!, $annotations: [AnnotationInput]!) {
    addAnnotation(videoId: $videoId, annotations: $annotations) {
      id
      annotations {
        id
        time
        text
        category
        color
        endTime
      }
    }
  }
`;

const FETCH_VIDEOS_QUERY = gql`
  query {
    videos {
      id
      url
      userId
      name
      size
      duration
      annotations {
        id
        time
        text
        category
        color
        endTime
      }
    }
  }
`;

const FETCH_VIDEO_QUERY = gql`
  query FetchVideo($videoId: String!) {
    video(videoId: $videoId) {
      id
      url
      userId
      name
      size
      duration
      frameRate
    }
  }
`;

export const uploadVideo = async (
  url: string,
  name: string,
  size: number,
  duration: string,
  token: string,
  storage?: { storageProvider: 'r2'; storageKey: string },
  frameRate?: number | null,
) => {
  try {
    const { data } = await client.mutate({
      mutation: UPLOAD_VIDEO_MUTATION,
      variables: {
        url,
        name,
        size,
        duration,
        storageProvider: storage?.storageProvider,
        storageKey: storage?.storageKey,
        frameRate: frameRate ?? null,
      },
      context: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    });
    return data.uploadVideo;
  } catch (error: any) {
    throw new Error(error.message);
  }
};

export const addAnnotation = async (videoId: string, annotations: { id: string; time: number; text: string; category?: string; color?: string; endTime?: number }[], token: string) => {
  try {
    const { data } = await client.mutate({
      mutation: ADD_ANNOTATION_MUTATION,
      variables: { videoId, annotations },
      context: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    });
    return data.addAnnotation;
  } catch (error: any) {
    throw new Error(error.message);
  }
};

export const fetchAnnotations = async (videoId: string, token: string) => {
  try {
    const { data } = await client.query({
      query: gql`
        query FetchAnnotations($videoId: String!) {
          annotations(videoId: $videoId) {
            id
            time
            text
            category
            color
            endTime
          }
        }
      `,
      variables: { videoId },
      context: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    });
    return data.annotations;
  } catch (error: any) {
    console.error('Error fetching annotations:', error);
    throw new Error(error.message);
  }
};

export const fetchVideos = async () => {
  try {
    const result = await withTransientRetry('Video library', () => client.query({
      query: FETCH_VIDEOS_QUERY,
      fetchPolicy: 'network-only',
      errorPolicy: 'none'
    }));

    // With `errorPolicy: all`, GraphQL errors do not throw by default.
    if (result.errors && result.errors.length > 0) {
      throw new Error(result.errors.map((e) => e.message).join('; '));
    }

    const videos = result.data?.videos;
    if (!Array.isArray(videos)) {
      throw new Error('No videos data returned from server');
    }

    // Set default values if fields are missing
    return videos.map((video: any) => ({
      id: video?.id || 'unknown',
      url: video?.url || '',
      userId: video?.userId || '',
      name: video?.name || 'Untitled Video',
      size: video?.size || 0,
      duration: video?.duration || '0:00',
      annotations: video?.annotations || []
    }));
  } catch (error: any) {
    console.error('Error fetching videos:', error);
    // Surface the error to the UI so testers don't see a silent empty library.
    throw new Error(error?.message || 'Failed to load videos');
  }
};

export const fetchVideoById = async (videoId: string, token: string) => {
  try {
    const { data } = await client.query({
      query: FETCH_VIDEO_QUERY,
      variables: { videoId },
      context: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    });
    return data.video;
  } catch (error: any) {
    console.error('Error fetching video:', error);
    throw new Error(error.message);
  }
};

export const submitEditingRequest = async (videoId: string, request: any, token: string) => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation SubmitEditingRequest($videoId: String!, $request: EditingRequestInput!) {
          submitEditingRequest(videoId: $videoId, request: $request) {
            id
            projectName
            status
          }
        }
      `,
      variables: { videoId, request },
      context: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    });
    return data.submitEditingRequest;
  } catch (error: any) {
    console.error('Error submitting editing request:', error);
    throw new Error(error.message);
  }
};

export const fetchEditingRequests = async (page: number, itemsPerPage: number, token: string) => {
  try {
    const { data } = await client.query({
      query: gql`
        query FetchEditingRequests($page: Int!, $itemsPerPage: Int!) {
          editingRequests(page: $page, itemsPerPage: $itemsPerPage) {
            requests {
              id
              projectName
              status
              videoId
              videoName
              userId
              createdAt
              updatedAt
              priority
              editorId
              editorName
              editorEmail
              revisionCount
              revisionLimit
              images {
                position
                startTime
                endTime
                url
              }
            }
            totalPages
          }
        }
      `,
      variables: { page, itemsPerPage },
      context: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    });
    
    // Handle null or undefined data
    if (!data || !data.editingRequests) {
      console.warn('No editing requests data returned from server');
      return { requests: [], totalPages: 0 };
    }
    
    // Map legacy status values to handle backend enum issues
    const mappedRequests = data.editingRequests.requests
      ?.filter((request: any) => request !== null && request !== undefined)
      ?.map((request: any) => ({
        ...request,
        status: request.status || 'VIDEO_SUBMITTED' // Default status if null
      })) || [];
    
    return { 
      ...data.editingRequests, 
      requests: mappedRequests 
    };
  } catch (error: any) {
    // Rethrow: returning an empty result made every backend or network
    // failure look like "you have no projects", and the dashboard showed
    // zero counts with no indication anything was wrong.
    console.error('Error fetching editing requests:', error);
    throw error;
  }
};

// In graphqlService.ts

export const fetchEditingDetails = async (requestId: string, token: string) => {
  try {
    const { data } = await client.query({
      query: gql`
        query FetchEditingDetails($requestId: ID!) {
          editingRequest(id: $requestId) {
            id
            videoId
            videoName
            videoDuration
            userId
            projectName
            description
            timing
            overlay
            textInstructions
            transitions {
              type
              startTime
              duration
              notes
            }
            images {
              position
              startTime
              endTime
              url
            }
            sounds {
              startTime
              endTime
              url
            }
            specialEffects {
              type
              startTime
              endTime
            }
            annotations {
              id
              time
              endTime
              text
              category
              color
            }
            status
            editedVideoPath
            comments
            editorId
            revisionCount
            revisionLimit
            creatorName
            deadline
            priority
            targetAudience
            desiredStyle
            musicPreference
            colorPreference
            brandGuidelines
            deliverables
            referenceLinks
            sourceFrameRate
            revisions {
              id
              filename
              createdBy
              createdByName
              createdAt
              status
              previewUrl
              notes
              sizeBytes
            }
            notes {
              id
              message
              authorId
              authorName
              authorRole
              timestamp
              type
            }
            editor {
              id
              email
              name
              status
            }
            tasks {
              id
              title
              description
              type
              timeline {
                start
                end
                frameStart
                frameEnd
              }
              priority
              status
              assetRefs
              assetInstructions
              aiInstructions
              estimatedMinutes
              actualMinutes
              dependencies
              tags
              editorNotes
              createdBy
              createdAt
              updatedAt
              completedAt
              reviewComments
              generatedFromAnnotations
            }
            taskProgress {
              totalTasks
              completedTasks
              inProgressTasks
              estimatedTotalMinutes
              actualTotalMinutes
              completionPercentage
              lastUpdated
            }
            createdAt
            updatedAt
            editorName
            editorEmail
            statusHistory {
              status
              timestamp
              userId
              userName
              comments
            }
            videoUrl
            assetManifest {
              totalAssets
              assetsByType {
                video
                image
                audio
                document
              }
              totalSizeBytes
              downloadUrl
              lastGenerated
            }
            canEditTasks
            sourceType
            multicamSetId
            multicamSnapshot {
              snapshotId
              createdAt
              name
              audioAngleKey
              timelineDurationMs
              angles {
                angleKey
                videoId
                label
                videoName
                videoUrl
                sizeBytes
                durationMs
                timelineStartMs
                sourceInMs
                sourceOutMs
              }
              audioSource {
                videoId
                label
                videoName
                videoUrl
                durationMs
                timelineStartMs
                sourceInMs
                sourceOutMs
                channels
                sampleRate
              }
            }
            multicamAnnotationsSnapshotId
          }
        }
      `,
      variables: { requestId },
      context: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    });
    return data.editingRequest;
  } catch (error: any) {
    console.error('Error fetching editing request details:', error);
    throw new Error(error.message);
  }
};

export const updateRequestStatus = async (requestId: string, status: string, comments: string | null, editorId: string | null, requestedEditor: string | null, token: string, editedVideoPath?: string) => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation UpdateRequestStatus($requestId: String!, $status: String!, $comments: String, $editorId: String, $requestedEditor: String, $editedVideoPath: String) {
          updateRequestStatus(requestId: $requestId, status: $status, comments: $comments, editorId: $editorId, requestedEditor: $requestedEditor, editedVideoPath: $editedVideoPath) {
            id
            status
            comments
            editorId
            requestedEditor
            editedVideoPath
          }
        }
      `,
      variables: { requestId, status, comments, editorId, requestedEditor, editedVideoPath },
      context: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    });
    return data.updateRequestStatus;
  } catch (error: any) {
    console.error('Error updating request status:', error);
    throw new Error(error.message);
  }
};

export const fetchEditors = async () => {
  try {
    const { data } = await withTransientRetry('Editors', () => client.query({
      query: gql`
        query GetEditors {
          editors {
            id
            email
            name
            status
          }
        }
      `,
      fetchPolicy: 'network-only',
      errorPolicy: 'none'
    }));
    return Array.isArray(data?.editors) ? data.editors.filter(Boolean) : [];
  } catch (error: any) {
    console.error('Error fetching editors:', error);
    throw new Error(error.message);
  }
};

export const fetchMyInvitations = async () => {
  try {
    const { data } = await client.query({
      query: gql`
        query GetMyInvitations {
          myInvitations {
            id
            email
            name
            status
            invitedBy
            invitedAt
            updatedAt
          }
        }
      `,
      fetchPolicy: 'network-only', // Always check for new invitations
    });
    return data.myInvitations;
  } catch (error: any) {
    console.error('Error fetching my invitations:', error);
    throw new Error(error.message);
  }
};

export const respondToInvitation = async (editorId: string, accept: boolean) => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation RespondToInvitation($editorId: ID!, $accept: Boolean!) {
          respondToInvitation(editorId: $editorId, accept: $accept) {
            id
            email
            name
            status
            firebaseUid
            acceptedAt
            updatedAt
          }
        }
      `,
      variables: { editorId, accept }
    });
    return data.respondToInvitation;
  } catch (error: any) {
    console.error('Error responding to invitation:', error);
    throw new Error(error.message || 'Failed to respond to invitation');
  }
};

export const addOrUpdateEditor = async (email: string, name: string, token: string) => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation AddOrUpdateEditor($email: String!, $name: String!) {
          addOrUpdateEditor(email: $email, name: $name) {
            id
            email
            name
            status
            invitationToken
            isExistingUser
            message
          }
        }
      `,
      variables: { email, name },
      context: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    });
    return data.addOrUpdateEditor;
  } catch (error: any) {
    console.error('Error adding/updating editor:', error);
    throw new Error(error.message);
  }
};

/**
 * Revisions go straight to R2 on the Cloudflare Worker, which rejects
 * multipart GraphQL requests and has no durable local disk. Only the legacy
 * Express server used for local development still accepts the Upload scalar.
 *
 * The default follows the build rather than an env file: the setting used to
 * live only in an untracked .env.production, so a production build from a
 * clean checkout fell back to multipart and every revision upload failed.
 */
export const resolveRevisionUploadMode = (): 'r2' | 'multipart' => {
  const configured = process.env.REACT_APP_REVISION_UPLOAD_MODE;
  if (configured === 'r2' || configured === 'multipart') return configured;
  return process.env.NODE_ENV === 'production' ? 'r2' : 'multipart';
};

export const uploadEditedVideo = async (
  requestId: string,
  file: File,
  notes: string,
  submitForReview: boolean,
  token: string,
  onProgress?: (percent: number) => void,
  options: { onResume?: (info: { uploadedBytes: number; totalBytes: number }) => void } = {}
) => {
  if (resolveRevisionUploadMode() === 'r2') {
    return uploadEditedVideoDirectly(
      requestId,
      file,
      notes,
      submitForReview,
      token,
      onProgress,
      options.onResume
    );
  }

  try {
    const operations = {
      query: `
        mutation UploadEditedVideo($requestId: String!, $file: Upload!, $notes: String, $submitForReview: Boolean) {
          uploadEditedVideo(requestId: $requestId, file: $file, notes: $notes, submitForReview: $submitForReview) {
            id
            status
            revisionCount
            revisions {
              id
              filename
              createdBy
              createdByName
              createdAt
              status
              previewUrl
              notes
              sizeBytes
            }
          }
        }
      `,
      variables: {
        requestId,
        file: null,
        notes: notes?.trim() ? notes.trim() : null,
        submitForReview
      }
    };

    const formData = new FormData();
    formData.append('operations', JSON.stringify(operations));
    formData.append('map', JSON.stringify({ '0': ['variables.file'] }));
    formData.append('0', file, file.name);

    const response = await fetch(getGraphQLEndpoint(), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`
      },
      body: formData
    });

    let payload: any;
    try {
      payload = await response.json();
    } catch (e) {
      const text = await response.text().catch(() => '');
      throw new Error(text || `Upload failed (${response.status})`);
    }

    if (!response.ok || payload?.errors?.length) {
      const message = payload?.errors?.[0]?.message || `Upload failed (${response.status})`;
      throw new Error(message);
    }

    return payload.data.uploadEditedVideo;
  } catch (error: any) {
    console.error('Error uploading edited video:', error);
    throw new Error(error.message || 'Failed to upload edited video');
  }
};

export const updateRevisionStatus = async (revisionId: string, status: string, comments: string = '', token: string) => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation UpdateRevisionStatus($revisionId: ID!, $status: String!, $comments: String) {
          updateRevisionStatus(revisionId: $revisionId, status: $status, comments: $comments) {
            id
            status
            notes
            previewUrl
            createdAt
            filename
            sizeBytes
          }
        }
      `,
      variables: { revisionId, status, comments },
      context: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    });
    return data.updateRevisionStatus;
  } catch (error: any) {
    console.error('Error updating revision status:', error);
    throw new Error(error.message);
  }
};

export const deleteProject = async (projectId: string, token: string) => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation DeleteProject($projectId: String!) {
          deleteProject(projectId: $projectId) {
            success
            message
          }
        }
      `,
      variables: { projectId },
      context: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    });
    return data.deleteProject;
  } catch (error: any) {
    console.error('Error deleting project:', error);
    throw new Error(error.message);
  }
};

export const fetchAllEditingRequests = async (page: number, itemsPerPage: number, token: string) => {
  try {
    const { data } = await client.query({
      query: gql`
        query FetchEditorRequests($page: Int!, $itemsPerPage: Int!) {
          allEditingRequests(page: $page, itemsPerPage: $itemsPerPage) {
            requests {
              id
              projectName
              status
              videoId
              videoName
              userId
              creatorName
              priority
              createdAt
              deadline
              editor {
                id
                email
                name
                status
              }
              tasks {
                id
                title
                description
                type
                status
                priority
                estimatedMinutes
                actualMinutes
                createdAt
                completedAt
              }
              taskProgress {
                totalTasks
                completedTasks
                inProgressTasks
                completionPercentage
                lastUpdated
              }
              sourceType
              multicamSnapshot {
                name
                audioAngleKey
                angles {
                  angleKey
                  label
                }
              }
            }
            totalPages
          }
        }
      `,
      variables: { page, itemsPerPage },
      context: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    });
    return data.allEditingRequests;
  } catch (error: any) {
    console.error('Error fetching editor requests:', error);
    throw new Error(error.message);
  }
};

export const updateEditor = async (editorId: string, name: string, token: string) => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation UpdateEditor($editorId: ID!, $name: String!) {
          updateEditor(editorId: $editorId, name: $name) {
            id
            email
            name
            status
          }
        }
      `,
      variables: { editorId, name },
      context: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    });
    return data.updateEditor;
  } catch (error: any) {
    console.error('Error updating editor:', error);
    throw new Error(error.message);
  }
};

export const removeEditor = async (editorId: string, token: string) => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation RemoveEditor($editorId: ID!) {
          removeEditor(editorId: $editorId)
        }
      `,
      variables: { editorId },
      context: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    });
    return data.removeEditor;
  } catch (error: any) {
    console.error('Error removing editor:', error);
    throw new Error(error.message);
  }
};

export const inviteEditor = async (email: string, name: string, token: string) => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation InviteEditor($email: String!, $name: String!) {
          inviteEditor(email: $email, name: $name) {
            id
            email
            name
          }
        }
      `,
      variables: { email, name },
      context: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    });
    return data.inviteEditor;
  } catch (error: any) {
    console.error('Error inviting editor:', error);
    throw new Error(error.message);
  }
};

export const mapExistingEditor = async (email: string, token: string) => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation MapExistingEditor($email: String!) {
          mapExistingEditor(email: $email) {
            id
            email
            name
          }
        }
      `,
      variables: { email },
      context: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    });
    return data.mapExistingEditor;
  } catch (error: any) {
    console.error('Error mapping editor:', error);
    throw new Error(error.message);
  }
};

export const updateEditorStatus = async (email: string, token: string) => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation UpdateEditorStatus($email: String!) {
          updateEditorStatus(email: $email) {
            id
            email
            name
            status
          }
        }
      `,
      variables: { email },
      context: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    });
    return data.updateEditorStatus;
  } catch (error: any) {
    console.error('Error updating editor status:', error);
    throw new Error(error.message);
  }
};

export const updateProjectStatus = async (requestId: string, status: string, comments: string, token: string) => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation UpdateProjectStatus($requestId: String!, $statusUpdate: StatusUpdateInput!) {
          updateProjectStatus(requestId: $requestId, statusUpdate: $statusUpdate) {
            id
            status
            statusHistory {
              status
              timestamp
              userId
              userName
              comments
            }
            updatedAt
          }
        }
      `,
      variables: { 
        requestId, 
        statusUpdate: { status, comments } 
      },
      context: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    });
    return data.updateProjectStatus;
  } catch (error: any) {
    console.error('Error updating project status:', error);
    throw new Error(error.message);
  }
};

/**
 * Assigns an editor. The server emails them (with `note`, if given) and keeps
 * the note on the project; the client no longer sends its own email.
 */
export const assignEditor = async (requestId: string, editorId: string, token: string, note?: string) => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation AssignEditor($requestId: String!, $editorId: String!, $note: String) {
          assignEditor(requestId: $requestId, editorId: $editorId, note: $note) {
            id
            editorId
            editor {
              id
              email
              name
              status
            }
            status
            statusHistory {
              status
              timestamp
              userId
              userName
              comments
            }
            updatedAt
          }
        }
      `,
      variables: { requestId, editorId, note: note?.trim() ? note.trim() : null },
      context: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    });
    return data.assignEditor;
  } catch (error: any) {
    console.error('Error assigning editor:', error);
    throw new Error(error.message);
  }
};

export const respondToProjectAssignment = async (requestId: string, accept: boolean, comments: string | null, token: string) => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation RespondToProjectAssignment($requestId: String!, $accept: Boolean!, $comments: String) {
          respondToProjectAssignment(requestId: $requestId, accept: $accept, comments: $comments) {
            id
            status
            editorId
            requestedEditor
            editorWorkflow {
              acceptedAt
            }
            statusHistory {
              status
              timestamp
              userId
              userName
              comments
            }
            updatedAt
          }
        }
      `,
      variables: { requestId, accept, comments },
      context: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    });
    return data.respondToProjectAssignment;
  } catch (error: any) {
    console.error('Error responding to project assignment:', error);
    throw new Error(error.message);
  }
};

export const deleteVideo = async (videoId: string, token: string) => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation DeleteVideo($videoId: String!) {
          deleteVideo(videoId: $videoId) {
            success
            message
          }
        }
      `,
      variables: { videoId },
      context: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    });
    return data.deleteVideo;
  } catch (error: any) {
    console.error('Error deleting video:', error);
    // Rethrow the ApolloError itself: the caller needs graphQLErrors to show
    // the backend's reason (the video is used by a project or multicam set).
    throw error;
  }
};

export const addProjectNote = async (projectId: string, message: string, type: string, token: string) => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation AddProjectNote($projectId: String!, $message: String!, $type: NoteType!) {
          addProjectNote(projectId: $projectId, message: $message, type: $type) {
            id
            notes {
              id
              message
              authorId
              authorName
              authorRole
              timestamp
              type
            }
          }
        }
      `,
      variables: { projectId, message, type },
      context: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    });
    return data.addProjectNote;
  } catch (error: any) {
    console.error('Error adding project note:', error);
    throw new Error(error.message);
  }
};

// Enhanced authentication with professional emails
export const registerUserWithEmails = async (
  uid: string, 
  email: string, 
  name: string, 
  role: string = 'creator',
  sendWelcome: boolean = true,
  verificationToken?: string
) => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation RegisterUserWithEmails(
          $uid: String!
          $email: String!
          $name: String!
          $role: String
          $sendWelcome: Boolean
          $verificationToken: String
        ) {
          registerUserWithEmails(
            uid: $uid
            email: $email
            name: $name
            role: $role
            sendWelcome: $sendWelcome
            verificationToken: $verificationToken
          ) {
            success
            user {
              uid
              email
              role
            }
            emailSent
            emailResults
            error
          }
        }
      `,
      variables: { uid, email, name, role, sendWelcome, verificationToken }
    });
    return data.registerUserWithEmails;
  } catch (error: any) {
    console.error('Error registering user with emails:', error);
    throw new Error(error.message);
  }
};

export const sendEmailVerificationProfessional = async (email: string, verificationToken: string) => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation SendEmailVerification($email: String!, $verificationToken: String!) {
          sendEmailVerification(email: $email, verificationToken: $verificationToken) {
            success
            messageId
            error
            verificationLink
          }
        }
      `,
      variables: { email, verificationToken }
    });
    return data.sendEmailVerification;
  } catch (error: any) {
    console.error('Error sending verification email:', error);
    throw new Error(error.message);
  }
};

export const sendPasswordResetEmailProfessional = async (email: string, resetToken: string) => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation SendPasswordResetEmail($email: String!, $resetToken: String!) {
          sendPasswordResetEmail(email: $email, resetToken: $resetToken) {
            success
            messageId
            error
            resetLink
          }
        }
      `,
      variables: { email, resetToken }
    });
    return data.sendPasswordResetEmail;
  } catch (error: any) {
    console.error('Error sending password reset email:', error);
    throw new Error(error.message);
  }
};

export const resetPasswordWithToken = async (token: string, newPassword: string) => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation ResetPasswordWithToken($token: String!, $newPassword: String!) {
          resetPasswordWithToken(token: $token, newPassword: $newPassword) {
            success
            message
            error
          }
        }
      `,
      variables: { token, newPassword }
    });
    return data.resetPasswordWithToken;
  } catch (error: any) {
    console.error('Error resetting password with token:', error);
    throw new Error(error.message || 'Failed to reset password');
  }
};

export const verifyEmailWithToken = async (token: string, email: string) => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation VerifyEmailWithToken($token: String!, $email: String!) {
          verifyEmailWithToken(token: $token, email: $email) {
            success
            message
            user {
              uid
              email
              role
            }
            error
          }
        }
      `,
      variables: { token, email }
    });
    return data.verifyEmailWithToken;
  } catch (error: any) {
    console.error('Error verifying email with token:', error);
    throw new Error(error.message);
  }
};

export const checkRateLimitStatus = async (email: string) => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation CheckRateLimitStatus($email: String!) {
          checkRateLimitStatus(email: $email) {
            success
            ipStatus { count resetTime allowed }
            emailStatus { count resetTime allowed }
            message
            error
          }
        }
      `,
      variables: { email }
    });
    return data.checkRateLimitStatus;
  } catch (error: any) {
    console.error('Error checking rate limit status:', error);
    throw new Error(error.message);
  }
};

export const sendProfessionalEditorInvitation = async (
  editorEmail: string, 
  editorName: string, 
  inviterName: string, 
  invitationLink: string
) => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation SendEditorInvitation(
          $editorEmail: String!
          $editorName: String!
          $inviterName: String!
          $invitationLink: String!
        ) {
          sendEditorInvitation(
            editorEmail: $editorEmail
            editorName: $editorName
            inviterName: $inviterName
            invitationLink: $invitationLink
          ) {
            success
            messageId
            error
          }
        }
      `,
      variables: { editorEmail, editorName, inviterName, invitationLink }
    });
    return data.sendEditorInvitation;
  } catch (error: any) {
    console.error('Error sending professional editor invitation:', error);
    throw new Error(error.message);
  }
};

export const acceptEditorInvitation = async (token: string, email: string) => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation AcceptEditorInvitation($token: String!, $email: String!) {
          acceptEditorInvitation(token: $token, email: $email) {
            success
            message
            isExistingUser
            redirectTo
            editor {
              id
              email
              name
              status
            }
            error
          }
        }
      `,
      variables: { token, email }
    });
    return data.acceptEditorInvitation;
  } catch (error: any) {
    console.error('Error accepting editor invitation:', error);
    throw new Error(error.message);
  }
};

export const declineEditorInvitation = async (token: string, email: string) => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation DeclineEditorInvitation($token: String!, $email: String!) {
          declineEditorInvitation(token: $token, email: $email) {
            success
            message
            isExistingUser
            redirectTo
            editor {
              id
              email
              name
              status
            }
            error
          }
        }
      `,
      variables: { token, email }
    });
    return data.declineEditorInvitation;
  } catch (error: any) {
    console.error('Error declining editor invitation:', error);
    throw new Error(error.message);
  }
};

// Enhanced Task Management Functions
export const generateProjectTasks = async (projectId: string, token: string, options?: {
  preserveManualTasks?: boolean;
  overwriteAITasks?: boolean;
  targetAnnotationIds?: string[];
  generateFromAllAnnotations?: boolean;
}) => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation GenerateProjectTasks($projectId: String!) {
          generateProjectTasks(projectId: $projectId) {
            success
            message
            tasksGenerated
            tasks {
              id
              title
              description
              type
              status
              priority
              estimatedMinutes
            }
          }
        }
      `,
      variables: { projectId },
      context: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    });
    return data.generateProjectTasks;
  } catch (error: any) {
    console.error('Error generating project tasks:', error);
    throw new Error(error.message);
  }
};

// Preview task generation changes without applying them
export const previewTaskGeneration = async (projectId: string, token: string, options?: {
  preserveManualTasks?: boolean;
  overwriteAITasks?: boolean;
  targetAnnotationIds?: string[];
  generateFromAllAnnotations?: boolean;
}) => {
  // TODO: Backend doesn't have previewTaskGeneration query yet
  console.warn('previewTaskGeneration not implemented in backend, using placeholder');
  return {
    changes: [],
    summary: {
      tasksToAdd: 0,
      tasksToModify: 0,
      tasksToDelete: 0,
      tasksToKeep: 0,
      annotationsProcessed: 0
    }
  };
};

export const enhanceTasksWithAI = async (projectId: string, token: string) => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation EnhanceTasksWithAI($projectId: String!) {
          enhanceTasksWithAI(projectId: $projectId) {
            success
            message
            tasksEnhanced
            aiProcessingTime
            tasks {
              id
              title
              description
              aiInstructions
              estimatedMinutes
            }
          }
        }
      `,
      variables: { projectId },
      context: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    });
    return data.enhanceTasksWithAI;
  } catch (error: any) {
    console.error('Error enhancing tasks with AI:', error);
    throw new Error(error.message);
  }
};

export const generateProjectZip = async (projectId: string, token: string) => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation GenerateProjectZip($projectId: String!) {
          generateProjectZip(projectId: $projectId) {
            success
            message
            downloadUrl
            filename
            sizeBytes
            expiresAt
          }
        }
      `,
      variables: { projectId },
      context: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    });
    const result = data.generateProjectZip;
    if (!result?.success || !result.downloadUrl) return result;

    const downloadResponse = await fetch(result.downloadUrl, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!downloadResponse.ok) {
      throw new Error(`Project ZIP download failed (${downloadResponse.status})`);
    }
    const objectUrl = window.URL.createObjectURL(await downloadResponse.blob());
    // Release the blob once the browser has had a chance to start the
    // download. Without this the entire ZIP stayed in memory for the life of
    // the tab, and assets can run to a gigabyte.
    window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
    return {
      ...result,
      downloadUrl: objectUrl,
      status: 'COMPLETED'
    };
  } catch (error: any) {
    console.error('Error generating project ZIP:', error);
    throw new Error(error.message);
  }
};



export const downloadAsset = async (
  projectId: string,
  assetUrl: string,
  assetType: string,
  token: string
) => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation DownloadAsset($projectId: String!, $assetUrl: String!, $assetType: String!) {
          downloadAsset(projectId: $projectId, assetUrl: $assetUrl, assetType: $assetType) {
            success
            message
            downloadUrl
            filename
            assetType
          }
        }
      `,
      variables: { projectId, assetUrl, assetType },
      context: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    });
    return data.downloadAsset;
  } catch (error: any) {
    console.error('Error downloading asset:', error);
    throw new Error(error.message);
  }
};

// Individual Task Management Functions
export const updateTaskStatus = async (projectId: string, taskId: string, status: string, notes?: string, token?: string) => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation UpdateTaskStatus($projectId: String!, $taskId: String!, $status: TaskStatus!, $notes: String) {
          updateTaskStatus(projectId: $projectId, taskId: $taskId, status: $status, notes: $notes) {
            id
            title
            status
            editorNotes
            completedAt
            updatedAt
          }
        }
      `,
      variables: { projectId, taskId, status, notes },
      context: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    });
    return data.updateTaskStatus;
  } catch (error: any) {
    console.error('Error updating task status:', error);
    throw new Error(error.message);
  }
};

export const updateTaskProgress = async (projectId: string, taskId: string, actualMinutes?: number, notes?: string, token?: string) => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation UpdateTaskProgress($projectId: String!, $taskId: String!, $actualMinutes: Int, $notes: String) {
          updateTaskProgress(projectId: $projectId, taskId: $taskId, actualMinutes: $actualMinutes, notes: $notes) {
            id
            title
            actualMinutes
            editorNotes
            updatedAt
          }
        }
      `,
      variables: { projectId, taskId, actualMinutes, notes },
      context: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    });
    return data.updateTaskProgress;
  } catch (error: any) {
    console.error('Error updating task progress:', error);
    throw new Error(error.message);
  }
};

// Enhanced Task Management Functions for Manual Creation/Editing
export const createTask = async (projectId: string, taskInput: any, token: string) => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation AddCustomTask($projectId: String!, $task: TaskInput!) {
          addCustomTask(projectId: $projectId, task: $task) {
            id
            title
            description
            type
            status
            priority
            timeline {
              start
              end
              frameStart
              frameEnd
            }
            assetRefs
            assetInstructions
            estimatedMinutes
            actualMinutes
            dependencies
            tags
            editorNotes
            createdAt
            updatedAt
          }
        }
      `,
      variables: { projectId, task: taskInput },
      context: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    });
    return data.addCustomTask;
  } catch (error: any) {
    console.error('Error creating task:', error);
    throw new Error(error.message);
  }
};

export const updateTask = async (projectId: string, taskId: string, taskInput: any, token: string) => {
  try {
    const updates: any = {};

    if (taskInput.title !== undefined) updates.title = taskInput.title;
    if (taskInput.description !== undefined) updates.description = taskInput.description;
    if (taskInput.type !== undefined) updates.type = taskInput.type;
    if (taskInput.priority !== undefined) updates.priority = taskInput.priority;
    if (taskInput.estimatedMinutes !== undefined) updates.estimatedMinutes = taskInput.estimatedMinutes;
    if (taskInput.tags !== undefined) updates.tags = taskInput.tags;
    if (taskInput.editorNotes !== undefined) updates.editorNotes = taskInput.editorNotes;
    if (taskInput.assetRefs !== undefined) updates.assetRefs = taskInput.assetRefs;
    if (taskInput.assetInstructions !== undefined) updates.assetInstructions = taskInput.assetInstructions;
    if (taskInput.dependencies !== undefined) updates.dependencies = taskInput.dependencies;
    if (taskInput.aiInstructions !== undefined) updates.aiInstructions = taskInput.aiInstructions;
    if (taskInput.reviewComments !== undefined) updates.reviewComments = taskInput.reviewComments;

    if (taskInput.timeline !== undefined) {
      updates.timeline = {
        start: taskInput.timeline.start,
        end: taskInput.timeline.end,
        frameStart: taskInput.timeline.frameStart,
        frameEnd: taskInput.timeline.frameEnd
      };
    }

    const { data } = await client.mutate({
      mutation: gql`
        mutation UpdateTask($projectId: String!, $taskId: String!, $updates: TaskUpdateInput!) {
          updateTask(projectId: $projectId, taskId: $taskId, updates: $updates) {
            id
            title
            description
            type
            status
            priority
            timeline {
              start
              end
              frameStart
              frameEnd
            }
            assetRefs
            assetInstructions
            aiInstructions
            estimatedMinutes
            actualMinutes
            dependencies
            tags
            editorNotes
            createdAt
            updatedAt
            completedAt
            reviewComments
            generatedFromAnnotations
          }
        }
      `,
      variables: { projectId, taskId, updates },
      context: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    });

    return data.updateTask;
  } catch (error: any) {
    console.error('Error updating task:', error);
    throw new Error(error.message);
  }
};

export const deleteTask = async (_projectId: string, taskId: string, token: string) => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation DeleteTask($taskId: String!) {
          deleteTask(taskId: $taskId) {
            success
            message
          }
        }
      `,
      variables: { taskId },
      context: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    });
    return data.deleteTask;
  } catch (error: any) {
    console.error('Error deleting task:', error);
    throw new Error(error.message);
  }
};

export const updateTaskTimeline = async (_projectId: string, taskId: string, timelineInput: any, token: string) => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation UpdateTaskTimeline($taskId: String!, $start: Float!, $end: Float!) {
          updateTaskTimeline(taskId: $taskId, start: $start, end: $end) {
            id
            timeline {
              start
              end
              frameStart
              frameEnd
            }
            updatedAt
          }
        }
      `,
      variables: {
        taskId,
        start: timelineInput.start,
        end: timelineInput.end
      },
      context: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    });

    return data.updateTaskTimeline;
  } catch (error: any) {
    console.error('Error updating task timeline:', error);
    throw new Error(error.message);
  }
};

// ============================================================================
// Multicam Mode Operations
// ============================================================================

// Types for multicam
export interface MulticamAngle {
  angleKey: string;
  videoId: string;
  label: string;
  videoName: string;
  videoUrl: string;
  sizeBytes: number;
  durationMs: number;
  timelineStartMs: number;
  sourceInMs: number;
  sourceOutMs: number | null;
  /** The person on this angle, for transcripts; empty means the angle's label. */
  speakerName?: string | null;
}

/** A separate sound recording lined up on the set's timeline; when set, it is the set's sound. */
export interface MulticamAudioSource {
  videoId: string;
  label: string;
  videoName: string;
  videoUrl: string;
  sizeBytes: number;
  durationMs: number;
  timelineStartMs: number;
  sourceInMs: number;
  sourceOutMs: number | null;
  channels?: number | null;
  sampleRate?: number | null;
}

/** What a plan unlocks: AI transcripts and suggested cuts are Pro Max, AI task suggestions Pro and up. */
export interface PlanFeatures {
  transcripts: boolean;
  suggestCuts: boolean;
  aiTaskSuggestions: boolean;
}

export interface MulticamSet {
  id: string;
  ownerUserId: string;
  name: string;
  status: 'DRAFT' | 'READY' | 'LOCKED';
  audioAngleKey: string;
  angles: MulticamAngle[];
  audioSource?: MulticamAudioSource | null;
  createdAt: string;
  updatedAt: string;
  /** Who speaks when (fetchMulticamSet only); null until worked out for the angles as they are. */
  speakingActivity?: SpeakingActivity | null;
  /** What the owner's plan unlocks (fetchMulticamSet only); missing means unknown, so nothing is held back here. */
  ownerFeatures?: PlanFeatures | null;
}

export interface MulticamAnnotation {
  id: string;
  multicamSetId: string;
  timelineTimeMs: number;
  endTimeMs: number | null;
  type: 'NOTE' | 'SWITCH';
  targetAngleKey: string | null;
  text: string;
  category: string;
  color: string;
  createdAt: string;
  updatedAt: string;
}

export interface MulticamSnapshot {
  snapshotId: string;
  createdAt: string;
  name: string;
  audioAngleKey: string;
  timelineDurationMs: number;
  angles: MulticamAngle[];
  audioSource?: MulticamAudioSource | null;
}

// GraphQL Fragments for reuse
const MULTICAM_ANGLE_FRAGMENT = gql`
  fragment MulticamAngleFields on MulticamAngle {
    angleKey
    videoId
    label
    videoName
    videoUrl
    sizeBytes
    durationMs
    timelineStartMs
    sourceInMs
    sourceOutMs
    speakerName
  }
`;

export const MULTICAM_AUDIO_SOURCE_FRAGMENT = gql`
  fragment MulticamAudioSourceFields on MulticamAudioSource {
    videoId
    label
    videoName
    videoUrl
    sizeBytes
    durationMs
    timelineStartMs
    sourceInMs
    sourceOutMs
    channels
    sampleRate
  }
`;

const MULTICAM_SET_FRAGMENT = gql`
  fragment MulticamSetFields on MulticamSet {
    id
    ownerUserId
    name
    status
    audioAngleKey
    angles {
      ...MulticamAngleFields
    }
    audioSource {
      ...MulticamAudioSourceFields
    }
    createdAt
    updatedAt
  }
  ${MULTICAM_ANGLE_FRAGMENT}
  ${MULTICAM_AUDIO_SOURCE_FRAGMENT}
`;

const MULTICAM_ANNOTATION_FRAGMENT = gql`
  fragment MulticamAnnotationFields on MulticamAnnotation {
    id
    multicamSetId
    timelineTimeMs
    endTimeMs
    type
    targetAngleKey
    text
    category
    color
    createdAt
    updatedAt
  }
`;

/**
 * Fetch all multicam sets for the current user
 */
export const fetchMulticamSets = async (limit: number = 20, offset: number = 0): Promise<MulticamSet[]> => {
  try {
    const { data } = await withTransientRetry('Multicam sets', () => client.query({
      query: gql`
        query MulticamSets($limit: Int, $offset: Int) {
          multicamSets(limit: $limit, offset: $offset) {
            ...MulticamSetFields
          }
        }
        ${MULTICAM_SET_FRAGMENT}
      `,
      variables: { limit, offset },
      fetchPolicy: 'network-only',
      errorPolicy: 'none'
    }));
    return Array.isArray(data?.multicamSets) ? data.multicamSets.filter(Boolean) : [];
  } catch (error: any) {
    console.error('Error fetching multicam sets:', error);
    throw new Error(error.message);
  }
};

/**
 * Fetch a single multicam set by ID
 */
export const fetchMulticamSet = async (multicamSetId: string): Promise<MulticamSet | null> => {
  try {
    const { data } = await client.query({
      query: gql`
        query MulticamSet($multicamSetId: String!) {
          multicamSet(multicamSetId: $multicamSetId) {
            ...MulticamSetFields
            ownerFeatures {
              transcripts
              suggestCuts
              aiTaskSuggestions
            }
            speakingActivity {
              version
              frameMs
              durationMs
              angleKeys
              confident
              reason
              runs
              basis
              computedAt
            }
          }
        }
        ${MULTICAM_SET_FRAGMENT}
      `,
      variables: { multicamSetId },
      fetchPolicy: 'network-only'
    });
    return data.multicamSet;
  } catch (error: any) {
    console.error('Error fetching multicam set:', error);
    throw new Error(error.message);
  }
};

export interface MulticamAngleConfig {
  angleKey: 'A' | 'B' | 'C' | 'D' | 'E' | 'F';
  videoId: string;
  label: string;
  timelineStartMs: number;
  sourceInMs: number;
  sourceOutMs: number | null;
  /** The person on this angle; omit to keep the name it has. */
  speakerName?: string;
}

/**
 * Create (no multicamSetId) or replace a multicam set's whole configuration
 * in one atomic server write. This replaced a chain of create/add/sync/audio
 * mutations that left a half-built set behind when one of them failed.
 *
 * When editing, pass the set's `updatedAt` as loaded in `expectedUpdatedAt`:
 * if the set was saved elsewhere since (another tab, another device), the
 * server refuses with MULTICAM_CONFLICT - thrown here as a
 * MulticamConflictError - instead of silently overwriting that save.
 */
export const saveMulticamSet = async (input: {
  multicamSetId?: string;
  name: string;
  audioAngleKey: string;
  angles: MulticamAngleConfig[];
  /** The separate recording; null removes it. */
  audioSource?: { videoId: string; label: string; timelineStartMs: number; sourceInMs: number; sourceOutMs: number | null } | null;
  expectedUpdatedAt?: string;
}): Promise<MulticamSet> => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation SaveMulticamSet($input: SaveMulticamSetInput!) {
          saveMulticamSet(input: $input) {
            ...MulticamSetFields
          }
        }
        ${MULTICAM_SET_FRAGMENT}
      `,
      variables: {
        input: {
          ...input,
          angles: input.angles.map((a) => ({
            angleKey: a.angleKey,
            videoId: a.videoId,
            label: a.label,
            timelineStartMs: a.timelineStartMs,
            sourceInMs: a.sourceInMs,
            // The GraphQL field is nullable; null means "play to the end".
            sourceOutMs: a.sourceOutMs,
            ...(a.speakerName !== undefined ? { speakerName: a.speakerName } : {}),
          })),
        },
      },
    });
    return data.saveMulticamSet;
  } catch (error: any) {
    console.error('Error saving multicam set:', error);
    // GraphQL errors normally arrive in graphQLErrors; a non-2xx response
    // carries them in the network error's body instead.
    const graphQLErrors: any[] = [
      ...(error?.graphQLErrors ?? []),
      ...(error?.networkError?.result?.errors ?? []),
    ];
    const conflict = graphQLErrors.find(
      (graphQLError) => graphQLError?.extensions?.code === MULTICAM_CONFLICT_CODE
    );
    if (conflict) {
      throw new MulticamConflictError(conflict.message || MULTICAM_CONFLICT_MESSAGE);
    }
    throw new Error(error.message);
  }
};

/**
 * Set multicam set status
 */
export const setMulticamStatus = async (multicamSetId: string, status: 'DRAFT' | 'READY' | 'LOCKED'): Promise<MulticamSet> => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation SetMulticamStatus($multicamSetId: String!, $status: MulticamSetStatus!) {
          setMulticamStatus(multicamSetId: $multicamSetId, status: $status) {
            ...MulticamSetFields
          }
        }
        ${MULTICAM_SET_FRAGMENT}
      `,
      variables: { multicamSetId, status }
    });
    return data.setMulticamStatus;
  } catch (error: any) {
    console.error('Error setting multicam status:', error);
    throw new Error(error.message);
  }
};

/**
 * Delete a multicam set
 */
export const deleteMulticamSet = async (multicamSetId: string): Promise<{ success: boolean; message: string }> => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation DeleteMulticamSet($multicamSetId: String!) {
          deleteMulticamSet(multicamSetId: $multicamSetId) {
            success
            message
            deletedAnnotationsCount
          }
        }
      `,
      variables: { multicamSetId }
    });
    return data.deleteMulticamSet;
  } catch (error: any) {
    console.error('Error deleting multicam set:', error);
    // Keep the ApolloError so the card can tell a backend refusal from a network failure.
    throw error;
  }
};

/**
 * Fetch annotations for a multicam set
 */
export const fetchMulticamAnnotations = async (multicamSetId: string, limit: number = 100, offset: number = 0): Promise<MulticamAnnotation[]> => {
  try {
    const { data } = await client.query({
      query: gql`
        query MulticamAnnotations($multicamSetId: String!, $limit: Int, $offset: Int) {
          multicamAnnotations(multicamSetId: $multicamSetId, limit: $limit, offset: $offset) {
            ...MulticamAnnotationFields
          }
        }
        ${MULTICAM_ANNOTATION_FRAGMENT}
      `,
      variables: { multicamSetId, limit, offset },
      fetchPolicy: 'network-only'
    });
    return data.multicamAnnotations;
  } catch (error: any) {
    console.error('Error fetching multicam annotations:', error);
    throw new Error(error.message);
  }
};

/**
 * Add an annotation to a multicam set
 */
export const addMulticamAnnotation = async (multicamSetId: string, annotation: {
  timelineTimeMs: number;
  endTimeMs?: number;
  type: 'NOTE' | 'SWITCH';
  targetAngleKey?: string | null;
  text: string;
  category?: string;
  color?: string;
}): Promise<MulticamAnnotation> => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation AddMulticamAnnotation($multicamSetId: String!, $annotation: MulticamAnnotationInput!) {
          addMulticamAnnotation(multicamSetId: $multicamSetId, annotation: $annotation) {
            ...MulticamAnnotationFields
          }
        }
        ${MULTICAM_ANNOTATION_FRAGMENT}
      `,
      variables: { multicamSetId, annotation }
    });
    return data.addMulticamAnnotation;
  } catch (error: any) {
    console.error('Error adding multicam annotation:', error);
    throw new Error(error.message);
  }
};

/**
 * Update a multicam annotation
 */
export const updateMulticamAnnotation = async (annotationId: string, updates: {
  timelineTimeMs?: number;
  /** null clears a range (the API stores whatever it is sent for a nullable Float). */
  endTimeMs?: number | null;
  type?: 'NOTE' | 'SWITCH';
  targetAngleKey?: string | null;
  text?: string;
  category?: string;
  color?: string;
}): Promise<MulticamAnnotation> => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation UpdateMulticamAnnotation($annotationId: String!, $updates: MulticamAnnotationInput!) {
          updateMulticamAnnotation(annotationId: $annotationId, updates: $updates) {
            ...MulticamAnnotationFields
          }
        }
        ${MULTICAM_ANNOTATION_FRAGMENT}
      `,
      variables: { annotationId, updates }
    });
    return data.updateMulticamAnnotation;
  } catch (error: any) {
    console.error('Error updating multicam annotation:', error);
    throw new Error(error.message);
  }
};

/**
 * Delete a multicam annotation
 */
export const deleteMulticamAnnotation = async (annotationId: string): Promise<boolean> => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation DeleteMulticamAnnotation($annotationId: String!) {
          deleteMulticamAnnotation(annotationId: $annotationId)
        }
      `,
      variables: { annotationId }
    });
    return data.deleteMulticamAnnotation;
  } catch (error: any) {
    console.error('Error deleting multicam annotation:', error);
    throw new Error(error.message);
  }
};

/**
 * Export annotations JSON for a multicam project
 */
export const exportAnnotationsJson = async (requestId: string): Promise<{
  version: string;
  exportedAt: string;
  multicamSetName: string;
  annotationCount: number;
  annotations: Array<{
    timelineTimeMs: number;
    endTimeMs: number | null;
    type: string;
    targetAngleKey: string | null;
    text: string;
    category: string | null;
    color: string | null;
  }>;
}> => {
  try {
    const { data } = await client.query({
      query: gql`
        query ExportAnnotationsJson($requestId: String!) {
          exportAnnotationsJson(requestId: $requestId) {
            version
            exportedAt
            multicamSetName
            annotationCount
            annotations {
              timelineTimeMs
              endTimeMs
              type
              targetAngleKey
              text
              category
              color
            }
          }
        }
      `,
      variables: { requestId },
      fetchPolicy: 'network-only'
    });
    return data.exportAnnotationsJson;
  } catch (error: any) {
    console.error('Error exporting annotations JSON:', error);
    throw new Error(error.message);
  }
};

/**
 * Check if a video is referenced by multicam sets or projects
 */
export const checkVideoReferences = async (videoId: string): Promise<{
  isReferenced: boolean;
  multicamSets: Array<{ id: string; name: string; angleKey: string }>;
  editingRequests: Array<{ id: string; projectName: string; sourceType: string }>;
}> => {
  try {
    const { data } = await client.query({
      query: gql`
        query CheckVideoReferences($videoId: String!) {
          checkVideoReferences(videoId: $videoId) {
            isReferenced
            multicamSets {
              id
              name
              angleKey
            }
            editingRequests {
              id
              projectName
              sourceType
            }
          }
        }
      `,
      variables: { videoId },
      fetchPolicy: 'network-only'
    });
    return data.checkVideoReferences;
  } catch (error: any) {
    console.error('Error checking video references:', error);
    throw new Error(error.message);
  }
};

/**
 * Submit an editing request with V2 API supporting multicam
 */
export const submitEditingRequestV2 = async (input: {
  sourceType: 'SINGLE' | 'MULTICAM';
  videoId?: string;
  multicamSetId?: string;
  request: any; // EditingRequestInput
}): Promise<any> => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation SubmitEditingRequestV2($input: SubmitEditingRequestV2Input!) {
          submitEditingRequestV2(input: $input) {
            id
            videoId
            videoName
            videoUrl
            userId
            projectName
            sourceType
            multicamSetId
            multicamSnapshot {
              snapshotId
              createdAt
              name
              audioAngleKey
              timelineDurationMs
              angles {
                angleKey
                videoId
                label
                videoName
                videoUrl
                sizeBytes
                durationMs
                timelineStartMs
                sourceInMs
                sourceOutMs
              }
              audioSource {
                videoId
                label
                videoName
                videoUrl
                durationMs
                timelineStartMs
                sourceInMs
                sourceOutMs
                channels
                sampleRate
              }
            }
            multicamAnnotationsSnapshotId
            status
            createdAt
          }
        }
      `,
      variables: { input }
    });
    return data.submitEditingRequestV2;
  } catch (error: any) {
    console.error('Error submitting editing request V2:', error);
    throw new Error(error.message);
  }
};

// ===== In-app Notifications =====

export interface InAppNotification {
  id: string;
  type: string;
  title: string;
  message: string;
  link?: string | null;
  relatedProjectId?: string | null;
  read: boolean;
  createdAt: string;
}

export const fetchMyNotifications = async (
  limit: number = 30,
  unreadOnly: boolean = false
): Promise<InAppNotification[]> => {
  try {
    const { data } = await client.query({
      query: gql`
        query MyNotifications($limit: Int, $unreadOnly: Boolean) {
          myNotifications(limit: $limit, unreadOnly: $unreadOnly) {
            id
            type
            title
            message
            link
            relatedProjectId
            read
            createdAt
          }
        }
      `,
      variables: { limit, unreadOnly },
      fetchPolicy: 'network-only'
    });
    return Array.isArray(data?.myNotifications) ? data.myNotifications.filter(Boolean) : [];
  } catch (error: any) {
    // Rethrown so the bell can tell "couldn't load" from "nothing new".
    console.error('Error fetching notifications:', error);
    throw error;
  }
};

interface DirectUploadIntent {
  uploadUrl: string;
  objectKey: string;
  method: 'PUT';
  headers: Array<{ name: string; value: string }>;
  expiresAt: string;
}

const postRevisionGraphQL = async (
  query: string,
  variables: Record<string, unknown>,
  token: string
) => {
  const response = await fetch(getGraphQLEndpoint(), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ query, variables })
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok || payload?.errors?.length) {
    throw new Error(payload?.errors?.[0]?.message || `Request failed (${response.status})`);
  }
  return payload.data;
};

const putFileWithProgress = (
  intent: DirectUploadIntent,
  file: File,
  onProgress?: (percent: number) => void
) => new Promise<void>((resolve, reject) => {
  const request = new XMLHttpRequest();
  request.open(intent.method, intent.uploadUrl);
  intent.headers.forEach(({ name, value }) => request.setRequestHeader(name, value));
  request.upload.onprogress = (event) => {
    if (event.lengthComputable && event.total > 0) {
      onProgress?.(Math.min(99, Math.round((event.loaded / event.total) * 100)));
    }
  };
  request.onerror = () => reject(new Error('The direct upload could not reach storage'));
  request.onabort = () => reject(new Error('The direct upload was cancelled'));
  request.onload = () => {
    if (request.status >= 200 && request.status < 300) {
      resolve();
    } else {
      reject(new Error(describeDirectUploadFailure(request.status)));
    }
  };
  request.send(file);
});

const REVISION_FIELDS = `
  id
  status
  revisionCount
  revisions {
    id
    filename
    createdBy
    createdByName
    createdAt
    status
    previewUrl
    notes
    sizeBytes
  }
`;

const uploadEditedVideoDirectly = async (
  requestId: string,
  file: File,
  notes: string,
  submitForReview: boolean,
  token: string,
  onProgress?: (percent: number) => void,
  onResume?: (info: { uploadedBytes: number; totalBytes: number }) => void
) => {
  try {
    // Large cuts go up in parts, resumable by choosing the same file again.
    if (file.size >= MULTIPART_THRESHOLD_BYTES) {
      return await uploadRevisionInParts(
        file,
        { requestId, contentType: file.type, notes, submitForReview },
        graphqlTransport(getGraphQLEndpoint(), () => currentIdToken(token)),
        { onProgress, onResume }
      );
    }
    onProgress?.(0);
    const intentData = await postRevisionGraphQL(
      // fileSizeBytes is a Float (the older `sizeBytes` argument is a 32-bit
      // Int and can't carry files of 2 GiB or more). The variable type must
      // match the argument exactly or GraphQL validation refuses the upload.
      // submitForReview lets the API refuse up front (e.g. while the creator
      // is still reviewing) instead of after the whole file has uploaded.
      `mutation CreateRevisionUpload($requestId: String!, $filename: String!, $contentType: String!, $fileSizeBytes: Float!, $submitForReview: Boolean) {
        createRevisionUpload(requestId: $requestId, filename: $filename, contentType: $contentType, fileSizeBytes: $fileSizeBytes, submitForReview: $submitForReview) {
          uploadUrl
          objectKey
          method
          headers { name value }
          expiresAt
        }
      }`,
      {
        requestId,
        filename: file.name,
        contentType: file.type,
        fileSizeBytes: file.size,
        submitForReview
      },
      token
    );
    const intent = intentData.createRevisionUpload as DirectUploadIntent;
    await putFileWithProgress(intent, file, onProgress);
    // A large cut can take longer to upload than the one-hour token above.
    const freshToken = await currentIdToken(token);
    const completed = await postRevisionGraphQL(
      `mutation CompleteRevisionUpload($requestId: String!, $objectKey: String!, $filename: String!, $contentType: String!, $fileSizeBytes: Float!, $notes: String, $submitForReview: Boolean) {
        completeRevisionUpload(requestId: $requestId, objectKey: $objectKey, filename: $filename, contentType: $contentType, fileSizeBytes: $fileSizeBytes, notes: $notes, submitForReview: $submitForReview) {
          ${REVISION_FIELDS}
        }
      }`,
      {
        requestId,
        objectKey: intent.objectKey,
        filename: file.name,
        contentType: file.type,
        fileSizeBytes: file.size,
        notes: notes?.trim() ? notes.trim() : null,
        submitForReview
      },
      freshToken
    );
    onProgress?.(100);
    return completed.completeRevisionUpload;
  } catch (error: any) {
    console.error('Error uploading edited video directly:', error);
    throw new Error(error.message || 'Failed to upload edited video');
  }
};

export const fetchUnreadNotificationCount = async (): Promise<number> => {
  try {
    const { data } = await client.query({
      query: gql`
        query MyUnreadNotificationCount {
          myUnreadNotificationCount
        }
      `,
      fetchPolicy: 'network-only'
    });
    return data?.myUnreadNotificationCount ?? 0;
  } catch (error: any) {
    // Rethrown so a failed check keeps the last known count instead of 0.
    console.error('Error fetching unread count:', error);
    throw error;
  }
};

export const markNotificationRead = async (notificationId: string): Promise<number> => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation MarkNotificationRead($notificationId: ID!) {
          markNotificationRead(notificationId: $notificationId) {
            success
            unreadCount
          }
        }
      `,
      variables: { notificationId }
    });
    return data?.markNotificationRead?.unreadCount ?? 0;
  } catch (error: any) {
    console.error('Error marking notification read:', error);
    throw error;
  }
};

export const markAllNotificationsRead = async (): Promise<number> => {
  try {
    const { data } = await client.mutate({
      mutation: gql`
        mutation MarkAllNotificationsRead {
          markAllNotificationsRead {
            success
            unreadCount
          }
        }
      `
    });
    return data?.markAllNotificationsRead?.unreadCount ?? 0;
  } catch (error: any) {
    console.error('Error marking all notifications read:', error);
    throw error;
  }
};

// ===== Creator Analytics =====

export interface CreatorAnalytics {
  totalProjects: number;
  activeProjects: number;
  completedProjects: number;
  cancelledProjects: number;
  completionRate: number;
  totalRevisions: number;
  avgRevisionsPerProject: number;
  totalEditors: number;
  activeEditors: number;
  storageUsedBytes: number;
  projectsByStatus: Array<{ status: string; count: number }>;
  projectsByMonth: Array<{ month: string; count: number }>;
}

export const fetchCreatorAnalytics = async (): Promise<CreatorAnalytics | null> => {
  try {
    const { data } = await withTransientRetry('Creator insights', () => client.query({
      query: gql`
        query CreatorAnalytics {
          creatorAnalytics {
            totalProjects
            activeProjects
            completedProjects
            cancelledProjects
            completionRate
            totalRevisions
            avgRevisionsPerProject
            totalEditors
            activeEditors
            storageUsedBytes
            projectsByStatus { status count }
            projectsByMonth { month count }
          }
        }
      `,
      fetchPolicy: 'network-only',
      errorPolicy: 'none'
    }));
    const analytics = data?.creatorAnalytics;
    if (!analytics) return null;
    return {
      ...analytics,
      projectsByStatus: Array.isArray(analytics.projectsByStatus) ? analytics.projectsByStatus.filter(Boolean) : [],
      projectsByMonth: Array.isArray(analytics.projectsByMonth) ? analytics.projectsByMonth.filter(Boolean) : [],
      totalEditors: Number(analytics.totalEditors || 0),
      activeEditors: Number(analytics.activeEditors || 0),
    };
  } catch (error: any) {
    console.error('Error fetching creator analytics:', error);
    return null;
  }
};

// ============================================================================
// Transcripts
// ============================================================================

export interface TranscriptWordData {
  text: string;
  /** Seconds of the video file. */
  start: number;
  end: number;
}

export interface TranscriptLineData {
  /** Seconds of the video file. */
  start: number;
  end: number;
  text: string;
  words?: TranscriptWordData[] | null;
}

export type TranscriptStatus = 'queued' | 'running' | 'done' | 'failed';

export interface VideoTranscriptData {
  id: string;
  videoId: string;
  status: TranscriptStatus;
  language: string | null;
  durationSeconds: number;
  chunkCount: number;
  chunksDone: number;
  /** 0 to 1 */
  progress: number;
  error: string | null;
  updatedAt: string;
  completedAt: string | null;
  /** Present when asked for. */
  segments?: TranscriptLineData[];
}

const VIDEO_TRANSCRIPT_FRAGMENT = gql`
  fragment VideoTranscriptFields on VideoTranscript {
    id
    videoId
    status
    language
    durationSeconds
    chunkCount
    chunksDone
    progress
    error
    updatedAt
    completedAt
  }
`;

/** A video's transcript (null when none was started), with its lines when `withLines`. */
export const fetchVideoTranscript = async (
  videoId: string,
  { withLines = true, withWords = false }: { withLines?: boolean; withWords?: boolean } = {}
): Promise<VideoTranscriptData | null> => {
  const { data } = await client.query({
    query: withLines
      ? gql`
          query VideoTranscript($videoId: String!, $withWords: Boolean) {
            videoTranscript(videoId: $videoId) {
              ...VideoTranscriptFields
              segments(withWords: $withWords) {
                start
                end
                text
                words {
                  text
                  start
                  end
                }
              }
            }
          }
          ${VIDEO_TRANSCRIPT_FRAGMENT}
        `
      : gql`
          query VideoTranscriptStatus($videoId: String!) {
            videoTranscript(videoId: $videoId) {
              ...VideoTranscriptFields
            }
          }
          ${VIDEO_TRANSCRIPT_FRAGMENT}
        `,
    variables: withLines ? { videoId, withWords } : { videoId },
    fetchPolicy: 'network-only',
  });
  return data?.videoTranscript ?? null;
};

/** Starts a transcript of a video's speech, or returns the one it has. */
export const startVideoTranscription = async (videoId: string, language?: string | null): Promise<VideoTranscriptData> => {
  const { data } = await client.mutate({
    mutation: gql`
      mutation StartVideoTranscription($videoId: String!, $language: String) {
        startVideoTranscription(videoId: $videoId, language: $language) {
          ...VideoTranscriptFields
        }
      }
      ${VIDEO_TRANSCRIPT_FRAGMENT}
    `,
    variables: { videoId, language: language ?? null },
  });
  return data.startVideoTranscription;
};

/**
 * Transcribes the next five minutes, if nobody else is (one model request,
 * usually 5 to 15 seconds). Returns the transcript's progress.
 */
export const advanceVideoTranscription = async (videoId: string): Promise<VideoTranscriptData | null> => {
  const { data } = await client.mutate({
    mutation: gql`
      mutation AdvanceVideoTranscription($videoId: String!) {
        advanceVideoTranscription(videoId: $videoId) {
          ...VideoTranscriptFields
        }
      }
      ${VIDEO_TRANSCRIPT_FRAGMENT}
    `,
    variables: { videoId },
  });
  return data?.advanceVideoTranscription ?? null;
};
